/**
 * **BGM 的启动策略** —— 纯策略，**不 import Tone、不 import 音频素材**。
 *
 * 真正驱动 `BgmDirector` 的适配器在 `audio/port.ts`；这里只回答三个问题：
 * 什么时候 load、什么时候 start、被浏览器拦下来怎么办。
 *
 * ## 为什么要拆开
 *
 * 浏览器**自动播放策略**要求音频必须由用户手势启动。这条策略有一堆边界情况
 * （`start()` 抛异常、手势来得比 `load()` 还早、用户点了别处……），
 * 而它在浏览器里**极难复现**（换成 headless 又是另一套行为）。
 * 把策略抽成"一个 port + 一个注册一次性手势的函数"，就能在 node 里
 * 用假 port 把每条分支都走一遍。
 *
 * ## 时序：**先解码，再谈起播**
 *
 * ```
 *   arm()  →  ① 挂上"一次性手势"监听   ← 只负责**记下**"玩家交互过了"
 *             ② load()                  ← 立刻开始解码（最慢的一步，不需要手势）
 *                  ↓ 解码完成
 *             ③ tryStart()              ← 现在起播**不用再等**：允许自动播就直接响
 *                  ↓ 被拒
 *             ④ 等（或补）那一次手势 → 在**手势自己的调用栈里**再 tryStart()
 * ```
 *
 * ### ⚠️ 为什么 start 一定要等到 load 完成之后
 *
 * `start()` 内部要 `await load()`，而素材有好几 MB —— 实测在软件渲染的
 * headless 里要 **4–5 秒**。如果不管三七二十一先 `start()`，那么玩家点下去之后
 * `start()` 还得先等解码走完，**那 4–5 秒的空档就变成了"点了没反应"**。
 *
 * 先解码、解码完再起播，点击到出声的间隔就只有 `context.resume()` 那一下。
 *
 * ### ⚠️ 那手势监听为什么还是要**一进来就挂**
 *
 * 因为玩家很可能在解码的那几秒里就点了。那时候我们起不了播（还没解码完），
 * 但**必须把这件事记下来** —— 否则等解码完，我们不知道"玩家其实已经交互过"，
 * 就会傻等一次永远不会再来的点击。
 *
 * 而且"玩家交互过"这件事是**粘性**的：浏览器（Chrome 的 sticky activation）
 * 记的是"这个文档被交互过"，不要求 `resume()` 就发生在事件处理器里。
 * 所以解码完成后拿着这个标记去起播是允许的，那个早点的点击没有白费。
 *
 * ### 为什么 start 必须串行
 *
 * `BgmDirector.start()` 在"还没加载完"时被并发调用会让两条链**各自**去
 * `buildRuntime()` —— 同一首曲子会建出两套播放器。所以这里用
 * `startInFlight` 保证任何时刻只有一个 `start()` 在跑。
 */

import type { ZenithBgmParams } from '../audio/score.zenith';

/**
 * 指挥器的**最小接口**。只声明我们真正用到的那几个方法 ——
 * 这样测试里给个假对象就够了，不需要把 Tone 拖进 node。
 */
export interface BgmPort {
  /** 预加载 + 解码。**不需要手势** */
  load(): Promise<void>;
  /** 开始播放。**必须在用户手势里调**（重复调用安全） */
  start(): Promise<void>;
  setParams(params: ZenithBgmParams): void;
  /** 0–1 */
  setVolume(value: number): void;
  /**
   * **BGM 的"播放故障"滤镜 + 混响剂量**（0..1）。见 `AudioSink.setBgmFx`。
   *
   * ⚠️ 它不改音量 —— 上一版那记"留白"被评审去掉了（无缝循环的 BGM 会被切成
   *    硬切）。这一路只改音色。
   */
  setFx(amount: number): void;
  /**
   * **音量增益倍数**（1 = 不动）。见 `AudioSink.setBgmBoost` —— 渐强用。
   *
   * ⚠️ 和 `setFx`（音色）分开：一个是响度、一个是质感。
   */
  setBoost(factor: number): void;
}

/**
 * **未开局时的 BGM 参数。**
 *
 * 评审：「未开始游戏时 isFinal，isDying 总是为假，energy 总是为 0。」
 *
 * 所以菜单里播的是**大厅曲**（`isFinal: false` → `lobby` cue），
 * 逆位层关着（`isDying: false`），随机装饰层一层都不开（`energy: 0`）。
 * 这三个值在整局开始之前**一次都不会变**，也不需要谁来"初始化"它们 ——
 * 没有对局，就没有别的可能值。
 */
export const PRE_GAME_BGM_PARAMS: ZenithBgmParams = Object.freeze({
  isFinal: false,
  isDying: false,
  energy: 0,
});

/** 注册一个"一次性手势"监听，返回取消函数。DOM 那半边的接口 */
export interface GestureSource {
  onFirstGesture(handler: () => void): () => void;
}

export interface BgmController {
  /** 越早调越好。load 立刻发起，start 尽力而为，失败就等一次性手势 */
  arm(): void;
  /** 0–1。会立刻应用（哪怕还没开始播） */
  setVolume(value: number): void;
  /** BGM 的"播放故障"剂量（0..1）。见 `BgmPort.setFx` */
  setFx(amount: number): void;
  /** 音量增益倍数（1 = 不动）。见 `BgmPort.setBoost` */
  setBoost(factor: number): void;
  /** 对局参数。开局前别调 —— 默认就是 `PRE_GAME_BGM_PARAMS` */
  setParams(params: ZenithBgmParams): void;
  /** 出声了没有。测试和调试用 */
  readonly started: boolean;
  /** 正在等一次性手势（`start()` 被浏览器拦下来了） */
  readonly awaitingGesture: boolean;
  /** 解码完了没有（起播的前置条件） */
  readonly ready: boolean;
  /** `true` 表示 load/start 出过错，页面上可以据此提示"没有声音" */
  readonly failed: boolean;
  dispose(): void;
}

export interface BgmControllerOptions {
  readonly port: BgmPort;
  readonly gesture: GestureSource;
  /**
   * **现在能不能出声**（`navigator.userActivation` 的封装）。
   *
   * 评审第六轮：
   *
   * > 我这边刷新后 BGM 不播放了。警告中多次显示"在用户操作之前试图自动播放"
   * > 相关的内容。你可以使用 `navigator.userActivation.isActive` 检测瞬态激活，
   * > 并仅在瞬态激活有效时开始播放，并取消事件监听。避免未成功播放就取消监听。
   *
   * 默认实现见 `transientActivation()`；**可以注入**（单测拿它造"没有激活"的场合）。
   */
  readonly activation?: () => boolean;
  /** 音量初值。通常是设置里存的那个 */
  readonly volume?: number;
  /** 出错时的回调。默认吞掉 —— 没有声音不该让游戏起不来 */
  readonly onError?: (error: unknown) => void;
}

/**
 * **瞬态激活**（`navigator.userActivation.isActive`）。
 *
 * - **有**这个 API：只认**瞬态**激活 —— 也就是"刚刚真的点过/按过"。
 *   浏览器就是拿这个判 `resume()` 该不该成功，顺着它走才不会再看到
 *   "在用户操作之前试图自动播放"。
 * - **没有**（老 Safari / node）：返回 `true`，把判断还给浏览器。
 */
export function transientActivation(): boolean {
  const nav = (globalThis as { navigator?: { userActivation?: { isActive?: boolean } } }).navigator;
  const active = nav?.userActivation?.isActive;
  return active === undefined ? true : active;
}

export function createBgmController(options: BgmControllerOptions): BgmController {
  const { port, gesture, onError } = options;

  let started = false;
  let awaiting = false;
  let failed = false;
  let disposed = false;
  /** 解码完成。**起播必须等它** —— 见文件头 */
  let loaded = false;
  /** 渐强倍数（1 = 不动）。见 `setBoost` */
  let boost = 1;
  /** 有一个 `start()` 正在飞。保证 start 串行 */
  let startInFlight = false;
  /** 挂着的取消函数。null = 没挂 */
  let pendingGesture: (() => void) | null = null;
  /** 记住要用的音量：起播之前也要能设，开始播时才有正确的响度 */
  let volume = typeof options.volume === 'number' ? options.volume : 1;

  const report = (error: unknown): void => {
    failed = true;
    onError?.(error);
  };

  /** 现在能不能起播。默认看瞬态激活，测试可以注入 */
  const canStart = (): boolean => (options.activation ?? transientActivation)();

  /**
   * **挂一次性手势。幂等。**
   *
   * ⚠️ **只有真的起播成功之后才摘掉监听**（评审："避免未成功播放就取消监听"）。
   *
   * 这里那个源是**一次性**的（`windowGestureSource` 触发后自己摘），所以每次
   * 触发之后：清了引用 → 判断能不能起播 → **不能就重新挂一个**。
   * 之前那版在失败路径上直接 `return`（而且 `gestureSeen` 把 `ensureGesture()`
   * 挡在门外），于是"第一次手势没成功"之后就**再也没有监听**了 ——
   * 现象正是评审报的"刷新后 BGM 不播放"。
   */
  const ensureGesture = (): void => {
    if (disposed || started || pendingGesture !== null) return;
    pendingGesture = gesture.onFirstGesture(() => {
      pendingGesture = null;
      awaiting = true;
      // 还没解码完：先什么都不做，`load()` 那条路会重新挂 / 起播
      if (!loaded || started) return;
      // ⚠️ 只在**瞬态激活还有效**时起播（就是这一次手势给的）：
      //    没有激活就去调 `start()` 只会换来一句自动播放警告
      if (canStart()) {
        tryStart();
        return;
      }
      ensureGesture();
    });
  };

  /** 试一次开始。串行；成功收工，失败就等/补那一次手势 */
  const tryStart = (): void => {
    if (disposed || started || startInFlight) return;
    startInFlight = true;
    void port
      .start()
      .then(() => {
        started = true;
        awaiting = false;
        startInFlight = false;
        // 成功之后把监听摘掉：留着的话以后每次点击都会再 start 一遍
        pendingGesture?.();
        pendingGesture = null;
      })
      .catch(() => {
        startInFlight = false;
        if (disposed || started) return;
        /**
         * ⚠️ **这不是错误**，是浏览器在按规矩办事：没有（瞬态的）用户激活就
         * 不许出声。所以不报给 `onError`，而是**继续等下一次手势** ——
         * `ensureGesture()` 会把监听重新挂上（刚才那个是一次性的）。
         */
        awaiting = true;
        ensureGesture();
      });
  };

  return {
    arm() {
      if (disposed) return;
      port.setVolume(volume);
      // ① 先挂上监听：只负责记下"玩家交互过了"，免得解码那几秒里的点击白费
      ensureGesture();
      // ② 立刻开始解码。它是最慢的一步，而且**不需要手势**
      void port
        .load()
        .then(() => {
          loaded = true;
          // ③ 解码完了才开始谈起播 —— 此刻 start() 不用再等，点了就响
          if (disposed || started) return;
          /**
           * ⚠️ 解码这几秒里玩家可能已经点过了：那时候的瞬态激活**早就过期**，
           *    现在直接 `start()` 只会挨一句自动播放警告（评审看到的那些）。
           *
           * 所以：激活还有效 → 起播；否则**重新挂监听**、并把
           * `awaitingGesture` 立起来（界面可以据此提示"点一下开启声音"）。
           *
           * 参考 MDN 的自动播放指南：判断"现在能不能出声"要看
           * `navigator.userActivation.isActive`（瞬态激活），而不是"用户以前
           * 点过没有"—— 见
           * https://developer.mozilla.org/zh-CN/docs/Web/Media/Guides/Autoplay
           */
          if (canStart()) tryStart();
          else {
            awaiting = true;
            ensureGesture();
          }
        })
        .catch(report);
    },
    setVolume(value) {
      volume = Number.isFinite(value) ? Math.min(Math.max(value, 0), 1) : 0;
      port.setVolume(volume);
    },
    setFx(amount) {
      // ⚠️ 不和 `volume` 揉在一起：一个是听感、一个是音色，各自独立
      port.setFx(Number.isFinite(amount) ? Math.min(Math.max(amount, 0), 1) : 0);
    },
    setBoost(factor) {
      boost = Number.isFinite(factor) ? Math.max(factor, 0) : 1;
      // ⚠️ 只加不减：渐强不该把玩家的音量设置改掉
      port.setVolume(volume * boost);
    },
    setParams(params) {
      port.setParams(params);
    },
    get started() {
      return started;
    },
    get awaitingGesture() {
      return awaiting;
    },
    get failed() {
      return failed;
    },
    /** 解码完了没有。测试和"要不要显示加载中"用它 */
    get ready() {
      return loaded;
    },
    dispose() {
      if (disposed) return;
      disposed = true;
      pendingGesture?.();
      pendingGesture = null;
    },
  };
}

/**
 * 把 `window` 上的**一次性**点击变成 `GestureSource`。
 *
 * 用 `pointerdown` 而不是 `click`：`click` 要等"按下 + 抬起"两件事都完成，
 * 而手指按下的那一刻浏览器就已经认可"用户交互"了 —— 早那几十毫秒在
 * "点了之后要等一会儿才有声"这件事上是能感觉到的。
 * 三种事件都挂，哪个先到算哪个。
 */
export function windowGestureSource(target: EventTarget = window): GestureSource {
  return {
    onFirstGesture(handler) {
      const events = ['pointerdown', 'keydown', 'click'] as const;

      const fire = (): void => {
        // 触发的那一个由浏览器按 `once` 摘掉；**另外两个**得我们自己摘
        for (const type of events) target.removeEventListener(type, fire);
        handler();
      };

      for (const type of events) {
        // ⚠️ 用 `addEventListener` **自带的** `once`，而不是自己拿一个布尔量挡：
        //    即使 `handler` 抛异常，浏览器也会把监听器摘掉 —— 少一处"忘了摘"。
        //    （三种事件各挂一次，所以还是要手动摘掉没触发的另外两个；
        //     重复 remove 是安全的空操作。）
        target.addEventListener(type, fire, { once: true, passive: true });
      }

      return () => {
        for (const type of events) target.removeEventListener(type, fire);
      };
    },
  };
}
